import {
	createSenderPreset,
	fetchSenderPresets,
	type SenderExternalChannelMode,
	type SenderRunPreset,
} from '@/api/sender-presets';
import { importPanelSenderSessionFile } from '@/api/sender-sessions';
import { createPanelSenderRun } from '@/api/sender-panel';
import LoadingWrapper from '@/components/loading-wrapper';
import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { Label } from '@/components/ui/label';
import { $features } from '@/entities/features-entity';
import { useUnit } from 'effector-react';
import { useSenderLaunchData } from '@/hooks/use-sender-launch-data';
import {
	type SenderSessionLoginConfirmedMeta,
	useSenderSessionLogin,
} from '@/hooks/use-sender-session-login';
import { resolveSenderActionError } from '@/lib/resolve-sender-action-error';
import {
	DEFAULT_SENDER_AUDIENCE_FILTER,
	isDeliveryProfileReady,
	resolveSenderAudienceFilter,
	shouldIncludeAudienceFilterInCreateRunForm,
	type SenderAudienceFilter,
} from '@/lib/sender-audience-filter';
import {
	DEFAULT_SENDER_APK_DELIVERY_METHOD,
	isLikelyExternalChannelUrl,
	resolveSenderApkDeliveryMethod,
	shouldRequireBuildForCreateRun,
	shouldRequireCaptionForCreateRun,
} from '@/lib/sender-apk-delivery';
import { setSenderBackOverride } from '@/pages/sender/sender-back-override';
import SenderWizardSteps from '@/pages/sender/components/sender-wizard-steps';
import { SenderScreen, senderPrimaryButtonClassName } from '@/pages/sender/sender-screen';
import type {
	SenderAccountMode,
	SenderLaunchLocationState,
	SenderLaunchWizardStep,
} from '@/pages/sender/launch-types';
import {
	launchDeliveryFieldsFromPreset,
	resolveInitialAccountMode,
} from '@/pages/sender/launch-wizard-utils';
import SenderAccountStep from '@/pages/sender/steps/sender-account-step';
import SenderDeliveryStep from '@/pages/sender/steps/sender-delivery-step';
import SenderReviewStep from '@/pages/sender/steps/sender-review-step';
import { useCallback, useEffect, useState } from 'react';
import { useBlocker, useLocation } from 'react-router';
import { toast } from 'sonner';

type DeliveryConfigMode = 'preset' | 'manual';

interface LaunchWizardProps {
	onStarted: (runId: string) => void;
}

function resolveAccountDisplayHint(meta: SenderSessionLoginConfirmedMeta): string {
	if (meta.label) return meta.label;
	if (meta.phone) return meta.phone;
	return '';
}

export default function LaunchWizard({ onStarted }: LaunchWizardProps) {
	const location = useLocation();
	const locationState = location.state as SenderLaunchLocationState | null;
	const { senderPresetsEnabled } = useUnit($features);

	const {
		builds,
		sessions,
		deliveryProfile,
		sessionsLoading,
		deliveryProfileLoading,
		deliveryProfileError,
		reload,
		retryDeliveryProfile,
	} = useSenderLaunchData();

	const [wizardStep, setWizardStep] = useState<SenderLaunchWizardStep>('account');
	const [accountMode, setAccountMode] = useState<SenderAccountMode>('phone');
	const [sessionId, setSessionId] = useState('');
	const [sessionFile, setSessionFile] = useState<File | null>(null);
	const [sessionFiles, setSessionFiles] = useState<File[]>([]);
	const [autoSpamAfterPhoneLogin, setAutoSpamAfterPhoneLogin] = useState(false);
	const [sessionSidecar, setSessionSidecar] = useState<File | null>(null);
	const [sessionLabel, setSessionLabel] = useState('');
	const [buildId, setBuildId] = useState('');
	const [caption, setCaption] = useState('');
	const [apkDeliveryMethod, setApkDeliveryMethod] = useState(DEFAULT_SENDER_APK_DELIVERY_METHOD);
	const [externalChannelUrl, setExternalChannelUrl] = useState('');
	const [audienceFilter, setAudienceFilter] = useState<SenderAudienceFilter>(DEFAULT_SENDER_AUDIENCE_FILTER);
	const [deleteAccountAfterRun, setDeleteAccountAfterRun] = useState(true);
	const [externalChannelMode, setExternalChannelMode] =
		useState<SenderExternalChannelMode>('latest_message');
	const [deliveryConfigMode, setDeliveryConfigMode] = useState<DeliveryConfigMode>('preset');
	const [presets, setPresets] = useState<SenderRunPreset[]>([]);
	const [presetsLoading, setPresetsLoading] = useState(false);
	const [selectedPresetId, setSelectedPresetId] = useState('');
	const [savePresetOpen, setSavePresetOpen] = useState(false);
	const [savePresetName, setSavePresetName] = useState('');
	const [savePresetSaving, setSavePresetSaving] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [initialized, setInitialized] = useState(false);
	const [accountDisplayHint, setAccountDisplayHint] = useState('');

	const selectedPreset = presets.find(p => p.id === selectedPresetId) ?? null;

	const onSessionConfirmed = useCallback(
		async (id: string, meta: SenderSessionLoginConfirmedMeta) => {
			setSessionId(id);
			setAccountMode('registry');
			setAccountDisplayHint(resolveAccountDisplayHint(meta));
			try {
				await reload();
			} catch (error) {
				toast.error(resolveSenderActionError(error, 'Не удалось обновить список сессий'));
			}
			setWizardStep('delivery');
		},
		[reload],
	);

	const phoneLogin = useSenderSessionLogin(onSessionConfirmed);

	const deliveryProfileReady = isDeliveryProfileReady(deliveryProfileLoading, deliveryProfile);
	const requireSidecar = deliveryProfile?.session.require_sidecar_json ?? false;

	useEffect(() => {
		if (initialized || sessionsLoading) return;
		const mode = resolveInitialAccountMode(locationState ?? undefined, sessions.length > 0);
		setAccountMode(mode);
		if (sessions.length > 0 && mode === 'registry') {
			setSessionId(sessions[0].id);
		}
		setInitialized(true);
	}, [initialized, sessions, sessionsLoading, locationState]);

	useEffect(() => {
		if (builds.length > 0 && !buildId) {
			setBuildId(builds[0].id);
		}
	}, [builds, buildId]);

	useEffect(() => {
		if (deliveryProfile?.audience.filter) {
			setAudienceFilter(resolveSenderAudienceFilter(deliveryProfile.audience.filter));
		}
	}, [deliveryProfile?.audience.filter]);

	function applyPresetSelection(presetId: string) {
		setSelectedPresetId(presetId);
		const preset = presets.find(p => p.id === presetId);
		if (!preset) return;
		const fields = launchDeliveryFieldsFromPreset(preset);
		setApkDeliveryMethod(fields.apkDeliveryMethod);
		setCaption(fields.caption);
		if (fields.buildId) setBuildId(fields.buildId);
		setExternalChannelUrl(fields.externalChannelUrl);
		setExternalChannelMode(fields.externalChannelMode);
		setAudienceFilter(fields.audienceFilter);
		setDeleteAccountAfterRun(fields.deleteAccountAfterRun);
	}

	useEffect(() => {
		if (!senderPresetsEnabled) {
			setDeliveryConfigMode('manual');
			return;
		}
		let cancelled = false;
		setPresetsLoading(true);
		void fetchSenderPresets()
			.then(items => {
				if (cancelled) return;
				setPresets(items);
				if (items.length > 0) {
					setSelectedPresetId(items[0].id);
					setDeliveryConfigMode('preset');
					const fields = launchDeliveryFieldsFromPreset(items[0]);
					setApkDeliveryMethod(fields.apkDeliveryMethod);
					setCaption(fields.caption);
					if (fields.buildId) setBuildId(fields.buildId);
					setExternalChannelUrl(fields.externalChannelUrl);
					setExternalChannelMode(fields.externalChannelMode);
					setAudienceFilter(fields.audienceFilter);
					setDeleteAccountAfterRun(fields.deleteAccountAfterRun);
				} else {
					setDeliveryConfigMode('manual');
				}
			})
			.catch(() => {
				if (!cancelled) {
					setPresets([]);
					setDeliveryConfigMode('manual');
				}
			})
			.finally(() => {
				if (!cancelled) setPresetsLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [senderPresetsEnabled]);

	useEffect(() => {
		setSenderBackOverride(() => {
			if (wizardStep === 'delivery') {
				setWizardStep('account');
				return true;
			}
			if (wizardStep === 'review') {
				setWizardStep('delivery');
				return true;
			}
			return false;
		});
		return () => setSenderBackOverride(null);
	}, [wizardStep]);

	const shouldBlockNavigation = phoneLogin.isPhoneLoginInProgress;
	const blocker = useBlocker(shouldBlockNavigation);

	useEffect(() => {
		if (blocker.state !== 'blocked') return;
		const leave = window.confirm(
			'Вход по номеру не завершён. Покинуть страницу? Незавершённая сессия на сервере может истечь.',
		);
		if (leave) {
			phoneLogin.resetPhoneLogin();
			blocker.proceed();
		} else {
			blocker.reset();
		}
	}, [blocker, phoneLogin]);

	useEffect(() => {
		if (!shouldBlockNavigation) return;
		function onBeforeUnload(e: BeforeUnloadEvent) {
			e.preventDefault();
		}
		window.addEventListener('beforeunload', onBeforeUnload);
		return () => window.removeEventListener('beforeunload', onBeforeUnload);
	}, [shouldBlockNavigation]);

	function validateAccountStep(): boolean {
		if (accountMode === 'registry') {
			if (!sessionId) {
				toast.error('Выберите сессию');
				return false;
			}
			return true;
		}
		if (!sessionFile) {
			toast.error('Выберите файл .session');
			return false;
		}
		if (requireSidecar && !sessionSidecar) {
			toast.error('Загрузите файл сессии');
			return false;
		}
		return true;
	}

	function validateDeliveryStep(): boolean {
		if (!deliveryProfileReady) {
			toast.error('Настройки рассылки ещё загружаются');
			return false;
		}
		if (deliveryConfigMode === 'preset') {
			if (!selectedPresetId) {
				toast.error('Выберите сценарий');
				return false;
			}
			return true;
		}
		const captionText = caption.trim();
		if (shouldRequireCaptionForCreateRun(apkDeliveryMethod)) {
			if (!captionText) {
				toast.error('Укажите текст сообщения');
				return false;
			}
			if (captionText.length > 1024) {
				toast.error('Текст сообщения не длиннее 1024 символов');
				return false;
			}
		}
		if (apkDeliveryMethod === 'external_channel') {
			const url = externalChannelUrl.trim();
			if (!url) {
				toast.error('Укажите ссылку на канал');
				return false;
			}
			if (externalChannelMode === 'post_link') {
				if (!/^https?:\/\/(www\.)?t\.me\/(?:c\/\d+\/\d+|[A-Za-z][A-Za-z0-9_]{3,31}\/\d+)/i.test(url)) {
					toast.error('Укажите ссылку на пост (t.me/channel/id)');
					return false;
				}
			} else if (!isLikelyExternalChannelUrl(url)) {
				toast.error('Некорректная ссылка на канал (только @username или t.me/… без поста)');
				return false;
			}
		}
		if (shouldRequireBuildForCreateRun(apkDeliveryMethod) && !buildId) {
			toast.error('Выберите билд APK');
			return false;
		}
		return true;
	}

	async function handleNext() {
		if (wizardStep === 'account') {
			if (accountMode === 'phone') {
				if (phoneLogin.phoneLoginStep === 'phone') {
					await phoneLogin.requestCode();
					return;
				}
				await phoneLogin.confirmCode();
				return;
			}
			if (!validateAccountStep()) return;
			if (accountMode === 'file' && sessionFiles.length > 1) {
				setIsSubmitting(true);
				try {
					let imported = 0;
					for (const file of sessionFiles.slice(1)) {
						await importPanelSenderSessionFile(file);
						imported += 1;
					}
					if (imported > 0) {
						toast.success(`Дополнительно импортировано сессий: ${imported}`);
						await reload();
					}
				} catch (error) {
					toast.error(resolveSenderActionError(error, 'Не удалось импортировать .session'));
					return;
				} finally {
					setIsSubmitting(false);
				}
			}
			setWizardStep('delivery');
			return;
		}
		if (wizardStep === 'delivery') {
			if (!validateDeliveryStep()) return;
			if (autoSpamAfterPhoneLogin && accountMode === 'phone') {
				await handleSubmit();
				return;
			}
			setWizardStep('review');
		}
	}

	async function handleSubmit() {
		if (!validateDeliveryStep()) {
			setWizardStep('delivery');
			return;
		}

		const captionText = caption.trim();
		const effectiveSessionId = accountMode === 'registry' || accountMode === 'phone' ? sessionId : '';
		if ((accountMode === 'registry' || accountMode === 'phone') && !effectiveSessionId) {
			toast.error('Сессия не выбрана');
			setWizardStep('account');
			return;
		}
		if (accountMode === 'file' && !sessionFile) {
			toast.error('Выберите файл .session');
			setWizardStep('account');
			return;
		}

		setIsSubmitting(true);
		try {
			const form = new FormData();
			if (deliveryConfigMode === 'preset' && selectedPresetId) {
				form.append('presetId', selectedPresetId);
			} else {
				form.append('apkDeliveryMethod', apkDeliveryMethod);
				if (apkDeliveryMethod === 'public_channel') {
					form.append('caption', captionText);
					form.append('buildId', buildId);
				} else {
					form.append('externalChannelUrl', externalChannelUrl.trim());
					if (externalChannelMode !== 'latest_message') {
						form.append('externalChannelMode', externalChannelMode);
					}
				}
				if (shouldIncludeAudienceFilterInCreateRunForm(deliveryProfileReady)) {
					form.append('audienceFilter', audienceFilter);
				}
				form.append('deleteAccountAfterRun', deleteAccountAfterRun ? 'true' : 'false');
			}
			if (accountMode === 'file' && sessionFile) {
				form.append('session', sessionFile);
			} else if (effectiveSessionId) {
				form.append('sessionId', effectiveSessionId);
			}
			if (accountMode === 'file' && sessionSidecar) {
				form.append('sessionSidecar', sessionSidecar);
			}
			if (sessionLabel.trim()) {
				form.append('sessionLabel', sessionLabel.trim());
			}
			const run = await createPanelSenderRun(form);
			toast.success('Запуск в очереди');
			onStarted(run.runId);
		} catch (error) {
			toast.error(resolveSenderActionError(error, 'Не удалось запустить рассылку'));
		} finally {
			setIsSubmitting(false);
		}
	}

	async function handleSaveAsPreset() {
		if (!validateDeliveryStep()) return;
		const name = savePresetName.trim();
		if (!name) {
			toast.error('Укажите название');
			return;
		}
		setSavePresetSaving(true);
		try {
			const created = await createSenderPreset({
				name,
				apkDeliveryMethod,
				caption: apkDeliveryMethod === 'public_channel' ? caption.trim() : null,
				buildId: apkDeliveryMethod === 'public_channel' ? buildId : null,
				externalChannelUrl:
					apkDeliveryMethod === 'external_channel' ? externalChannelUrl.trim() : null,
				externalChannelMode,
				audienceFilter,
				deleteAccountAfterRun,
			});
			setPresets(prev => [created, ...prev]);
			setSelectedPresetId(created.id);
			setDeliveryConfigMode('preset');
			setSavePresetOpen(false);
			setSavePresetName('');
			toast.success('Сценарий сохранён');
		} catch (error) {
			toast.error(resolveSenderActionError(error, 'Не удалось сохранить сценарий'));
		} finally {
			setSavePresetSaving(false);
		}
	}

	const nextDisabled =
		wizardStep === 'delivery' &&
		(!deliveryProfileReady ||
			presetsLoading ||
			(deliveryConfigMode === 'preset' && !selectedPresetId) ||
			(deliveryConfigMode === 'manual' &&
				shouldRequireBuildForCreateRun(apkDeliveryMethod) &&
				builds.length === 0));

	const stepTitle = wizardStep === 'account' ? 'Сессия' : wizardStep === 'delivery' ? 'Что слать' : 'Старт';
	const stepIndex = wizardStep === 'account' ? 0 : wizardStep === 'delivery' ? 1 : 2;

	return (
		<SenderScreen
			title={stepTitle}
			lead={<SenderWizardSteps steps={['Сессия', 'Что слать', 'Старт']} currentIndex={stepIndex} />}
			footer={
				wizardStep === 'review' ? (
					<Button
						type='button'
						className={senderPrimaryButtonClassName()}
						disabled={isSubmitting}
						onClick={() => void handleSubmit()}
					>
						Запустить
					</Button>
				) : (
					<Button
						type='button'
						className={senderPrimaryButtonClassName()}
						disabled={nextDisabled || isSubmitting}
						onClick={() => void handleNext()}
					>
						{wizardStep === 'account' && accountMode === 'phone'
							? phoneLogin.phoneLoginStep === 'phone'
								? 'Запросить код'
								: 'Подтвердить'
							: 'Дальше'}
					</Button>
				)
			}
		>
			<LoadingWrapper show={isSubmitting}>
			<div className='flex flex-col gap-4'>
				{sessionsLoading && wizardStep === 'account' ? (
					<div className='flex justify-center py-6'>
						<Spinner className='size-5' />
					</div>
				) : null}
				{wizardStep === 'account' ? (
					<SenderAccountStep
						showModePicker
						inlinePhoneActions={false}
						sessions={sessions}
						sessionsLoading={sessionsLoading}
						requireSidecar={requireSidecar}
						accountMode={accountMode}
						onAccountModeChange={setAccountMode}
						sessionId={sessionId}
						onSessionIdChange={setSessionId}
						sessionFile={sessionFile}
						sessionFiles={sessionFiles}
						onSessionFileChange={setSessionFile}
						onSessionFilesChange={setSessionFiles}
						sessionSidecar={sessionSidecar}
						onSessionSidecarChange={setSessionSidecar}
						sessionLabel={sessionLabel}
						onSessionLabelChange={setSessionLabel}
						phoneLoginStep={phoneLogin.phoneLoginStep}
						phone={phoneLogin.phone}
						onPhoneChange={phoneLogin.setPhone}
						phoneLabel={phoneLogin.label}
						onPhoneLabelChange={phoneLogin.setLabel}
						code={phoneLogin.code}
						onCodeChange={phoneLogin.setCode}
						codeType={phoneLogin.codeType}
						onRequestCode={() => void phoneLogin.requestCode()}
						onConfirmCode={() => void phoneLogin.confirmCode()}
						phoneLoginSaving={phoneLogin.isSaving}
						autoSpamAfterPhoneLogin={autoSpamAfterPhoneLogin}
						onAutoSpamAfterPhoneLoginChange={setAutoSpamAfterPhoneLogin}
					/>
				) : null}

				{wizardStep === 'delivery' ? (
					<div className='flex flex-col gap-4'>
						{senderPresetsEnabled && presets.length > 0 ? (
							<div className='flex flex-col gap-2'>
								<p className='text-[15px]'>Сценарий</p>
								{presets.map(preset => (
									<button
										key={preset.id}
										type='button'
										className='min-h-11 rounded-xl border border-border px-3 py-2 text-left text-[15px]'
										onClick={() => {
											applyPresetSelection(preset.id);
											setDeliveryConfigMode('manual');
										}}
									>
										{preset.name}
									</button>
								))}
							</div>
						) : null}

						<>
								<SenderDeliveryStep
									deliveryProfileReady={deliveryProfileReady}
									deliveryProfileError={deliveryProfileError}
									onRetryDeliveryProfile={() => void retryDeliveryProfile()}
									apkDeliveryMethod={apkDeliveryMethod}
									onApkDeliveryMethodChange={v =>
										setApkDeliveryMethod(resolveSenderApkDeliveryMethod(v))
									}
									caption={caption}
									onCaptionChange={setCaption}
									externalChannelUrl={externalChannelUrl}
									onExternalChannelUrlChange={setExternalChannelUrl}
									externalChannelMode={externalChannelMode}
									onExternalChannelModeChange={setExternalChannelMode}
									audienceFilter={audienceFilter}
									onAudienceFilterChange={setAudienceFilter}
									builds={builds}
									buildId={buildId}
									onBuildIdChange={setBuildId}
								/>
								{senderPresetsEnabled ? (
									<Button
										type='button'
										variant='secondary'
										className='self-start'
										onClick={() => setSavePresetOpen(true)}
									>
										Сохранить как сценарий
									</Button>
								) : null}
						</>
					</div>
				) : null}

				{wizardStep === 'review' ? (
					<SenderReviewStep
						accountMode={accountMode}
						sessionId={sessionId}
						sessions={sessions}
						accountDisplayHint={accountDisplayHint}
						sessionFile={sessionFile}
						sessionLabel={sessionLabel}
						presetName={
							deliveryConfigMode === 'preset' ? (selectedPreset?.name ?? '') : undefined
						}
						apkDeliveryMethod={
							deliveryConfigMode === 'preset' && selectedPreset
								? selectedPreset.apkDeliveryMethod
								: apkDeliveryMethod
						}
						caption={
							deliveryConfigMode === 'preset' && selectedPreset
								? (selectedPreset.caption ?? '')
								: caption
						}
						externalChannelUrl={
							deliveryConfigMode === 'preset' && selectedPreset
								? (selectedPreset.externalChannelUrl ?? '')
								: externalChannelUrl
						}
						audienceFilter={
							deliveryConfigMode === 'preset' && selectedPreset
								? selectedPreset.audienceFilter
								: audienceFilter
						}
						builds={builds}
						buildId={
							deliveryConfigMode === 'preset' && selectedPreset
								? (selectedPreset.buildId ?? '')
								: buildId
						}
						deleteAccountAfterRun={
							deliveryConfigMode === 'preset' && selectedPreset
								? selectedPreset.deleteAccountAfterRun
								: deleteAccountAfterRun
						}
						onDeleteAccountAfterRunChange={setDeleteAccountAfterRun}
						deliveryLocked={false}
					/>
				) : null}
			</div>

			<Dialog open={savePresetOpen} onOpenChange={setSavePresetOpen}>
				<DialogContent className='sm:max-w-md'>
					<DialogHeader>
						<DialogTitle>Сохранить как сценарий</DialogTitle>
					</DialogHeader>
					<div className='flex flex-col gap-2'>
						<Label htmlFor='save-preset-name'>Название</Label>
						<Input
							id='save-preset-name'
							value={savePresetName}
							maxLength={64}
							onChange={e => setSavePresetName(e.target.value)}
						/>
					</div>
					<DialogFooter>
						<Button type='button' variant='outline' onClick={() => setSavePresetOpen(false)}>
							Отмена
						</Button>
						<Button
							type='button'
							disabled={savePresetSaving}
							onClick={() => void handleSaveAsPreset()}
						>
							Сохранить
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
			</LoadingWrapper>
		</SenderScreen>
	);
}
